import { Formik } from 'formik';
import * as React from 'react';
import * as Yup from 'yup';
import { useKioskState } from '../../../context/Kiosk/kiosk';
import { useLocalization, useLocalizationExtension } from '../../../context/Localization/localization';
import { useExitToDesktop, useHardwareProviderState } from '../../../context/hardware';
import { CTAButton } from '../../../controls/ButtonBase';
import { Heading } from '../../../controls/Heading/Heading';
import { ExitToDeskTopDialog } from './ExitToDeskTopDialog/ExitToDesktopDialog';
import { VersionInfo } from '../../../controls/VersionInfo/VersionInfo';
import { KeyboardLayouts } from '../../../controls/SimpleKeyboard/Layout';
import { useAdminLogin } from '../../../context/Admin/admin';
import { nameof } from '../../../helpers/nameof';
import { GMFormPasswordField } from '../../../controls/TextField';
import { GMDialogError } from '../../../controls/Dialog';
import { GMRoundedIcon, GMSizedIcon } from '../../../controls/Icon';
import { GMForm, GMFormWrapper } from '../../../controls/Form';
import { isVSAKiosk } from '../../../helpers/EnvironmentFlags/EnvironmentFlags';

interface IProps {
    onProceed: () => void;
}

export const AdminPin = (props: IProps) => {
    const Localization = useLocalization();
    const { formatRequired, formatInvalid } = useLocalizationExtension();
    const initialValues: { pin?: string; } = {};
    const {
        key: kioskKey,
        kioskType,
        dealer: { key: dealerKey },
    } = useKioskState();
    const [showExitToDesktopPopup, setShowExitToDesktopPopup] = React.useState(false);
    const { exit } = useExitToDesktop();
    const [pinInvalid, setPinInvalid] = React.useState(false);
    const { isLinux } = useHardwareProviderState();
    const [error, setError] = React.useState<string | undefined>(undefined);
    const validate = useAdminLogin();

    React.useEffect(() => {
        if (!isLinux) {
            window.addEventListener('keydown', exitToDesktopPopup);
        }

        // Removing event listeners on cleanup
        return () => {
            if (!isLinux) {
                window.removeEventListener('keydown', exitToDesktopPopup);
            }
        };
    }, [isLinux]);

    const exitToDesktopPopup = (event: { metaKey: any; altKey: any; keyCode: number; }) => {
        if ((event.metaKey || event.altKey) && event.keyCode === 116) {
            console.log('We are in function exitToDesktopPopup');
            setShowExitToDesktopPopup(true);
        }
    };

    const exitToDesktop = (exitPin: string) => {
        console.warn('We are in function exitToDesktop');
        if (parseInt(exitPin) === 7777 && exit) {
            console.warn('ExitToDesktop pin matched');
            exit('Admin');
        }
    };

    return (
        <>
            <VersionInfo />
            <Formik
                initialValues={initialValues}
                validate={async (values) => {
                    if (pinInvalid) {
                        if (isVSAKiosk(kioskType)) {
                            return { [nameof<typeof initialValues>('pin')]: formatInvalid(Localization.Kiosk) };
                        }
                        else {
                            return { [nameof<typeof initialValues>('pin')]: formatInvalid(Localization.PIN) };
                        }
                    }
                    else {
                        return await Yup.object()
                            .shape({
                                [nameof<typeof initialValues>('pin')]: Yup.number()
                                    .required(formatRequired(Localization.PIN))
                                    .typeError(formatInvalid(Localization.PIN)),
                            })
                            .validate(values)
                            .then(
                                () => ({}),
                                (error) => ({ [error.path]: error.message }),
                            );
                    }
                }}
                onSubmit={async (values, { setFieldTouched, setFieldError }) => {
                    if (isVSAKiosk(kioskType)) {
                        setPinInvalid(true);
                        setFieldError(nameof<typeof initialValues>('pin'), formatInvalid(Localization.Kiosk));

                        return;
                    }

                    if (!values.pin) {
                        setPinInvalid(true);
                        setFieldError(nameof<typeof initialValues>('pin'), formatInvalid(Localization.PIN));

                        return;
                    }

                    const result = await validate({
                        kioskKey,
                        dealerKey,
                        pin: values.pin,
                    });
                    if (result.error) {
                        setError(result.message);
                        setPinInvalid(true);
                        setFieldError(nameof<typeof initialValues>('pin'), formatInvalid(Localization.PIN));
                    }
                    else {
                        props.onProceed();
                    }
                }}
            >
                <GMForm
                    heading={
                        <Heading
                            primaryHeading={Localization.AdminLogin}
                            secondaryHeading={Localization.PleaseEnterYourPIN}
                            roundedIcon={
                                <GMRoundedIcon roundedBackgroundSize={'large'} themeType={'secondary'}>
                                    <GMSizedIcon iconType={'Key'} iconSize={'large'} />
                                </GMRoundedIcon>
                            }
                        />
                    }
                    submitButton={
                        <CTAButton
                            styleType={'primary'}
                            type={'submit'}
                            title={Localization.ClickToProceedToNextStep}
                            label={Localization.Proceed}
                            ariaLabel={Localization.ClickToProceedToNextStep}
                            id={Localization.ClickToProceedToNextStep}
                        />
                    }
                >
                    <GMFormWrapper finePrint requiredFieldLabel>{Localization.RequiredFields}</GMFormWrapper>
                    <GMFormPasswordField
                        name={nameof<typeof initialValues>('pin')}
                        id={nameof<typeof initialValues>('pin')}
                        title={Localization.PIN}
                        label={Localization.PIN}
                        ariaLabel={Localization.PIN}
                        keyboardLayout={KeyboardLayouts.NumberPad}
                        onChangeCallback={() => setPinInvalid(false)}
                        autoFocus
                        required
                    />
                </GMForm>
            </Formik>
            <GMDialogError
                ariaLabel={Localization.AnErrorHasOccured}
                open={!!error}
                onClose={() => setError(undefined)}
            >
                {error}
            </GMDialogError>

            <ExitToDeskTopDialog
                open={showExitToDesktopPopup}
                onClose={() => setShowExitToDesktopPopup(false)}
                onConfirm={async (newPin) => {
                    exitToDesktop(newPin);
                }}
            />
        </>
    );
};
